import SelectPrimitive from 'components/primitives/components/Select';
import { forwardRef } from 'react';

import { useController } from 'react-hook-form';

type SelectProps = Omit<React.ComponentPropsWithoutRef<typeof SelectPrimitive>, 'name' | 'onValueChange'> & {
    name: string;
} & (
        | { valueAsNumber: true; onValueChange?: (value: number) => void }
        | { valueAsNumber?: false; onValueChange?: (value: string) => void }
    );

/**
 * Renders a select component that can be used with react-hook-form.
 *
 * @example
 * ```tsx
 * <Select name="select" onValueChange={(value) => console.log(value)}>
 *   <Select.TriggerButton>
 *     <Select.Value placeholder="Select an option" />
 *   </Select.TriggerButton>
 *   <Select.Content>
 *     <Select.Group>
 *       <Select.Label>Label</Select.Label>
 *       <Select.Item value="item1">Item 1</Select.Item>
 *       <Select.Item value="item2">Item 2</Select.Item>
 *     </Select.Group>
 *   </Select.Content>
 * </Select>
 * ```
 */
const Select = ({ children, name, onValueChange, valueAsNumber, ...props }: SelectProps) => {
    const { field } = useController({
        name
    });

    return (
        <SelectPrimitive
            onValueChange={(value) => {
                if (valueAsNumber) {
                    const numberValue = Number(value);
                    onValueChange?.(numberValue);
                    field?.onChange(numberValue);
                    return;
                }

                onValueChange?.(value);
                field?.onChange(value);
            }}
            name={name}
            // Ensure the value is controlled
            value={field.value?.toString() ?? ''}
            {...props}
        >
            {children}
        </SelectPrimitive>
    );
};

/**
 * Renders a button that triggers the select dropdown.
 *
 * @example
 * ```tsx
 * <Select.TriggerButton>
 *   <Select.Value placeholder='Select an option' />
 * </Select.TriggerButton>
 */
const TriggerButton = forwardRef<
    React.ElementRef<typeof SelectPrimitive.Trigger>,
    React.ComponentPropsWithoutRef<typeof SelectPrimitive.TriggerButton>
>(({ children, variant = 'input', ...props }, ref) => (
    <SelectPrimitive.TriggerButton ref={ref} variant={variant} {...props}>
        {children}
    </SelectPrimitive.TriggerButton>
));

export default Object.assign(Select, {
    Content: SelectPrimitive.Content,
    Trigger: SelectPrimitive.Trigger,
    Group: SelectPrimitive.Group,
    Item: SelectPrimitive.Item,
    Label: SelectPrimitive.Label,
    Separator: SelectPrimitive.Separator,
    TriggerButton,
    Value: SelectPrimitive.Value
});
